'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { clsx } from 'clsx';
import type { ElementType } from 'react';
import { useNavHighlight } from '@paeffgen/react/crm-documents/ui';
import { HIGHLIGHT_KEY_FOR_ROUTE } from './nav-config';

export interface NavItem {
  href: string;
  label: string;
  icon: ElementType;
  disabled?: boolean;
}

interface MobileBottomNavProps {
  items: NavItem[];
  materialanfrageCount?: number;
}

export function MobileBottomNav({
  items,
  materialanfrageCount = 0,
}: MobileBottomNavProps) {
  const pathname = usePathname();
  const { highlights } = useNavHighlight();

  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-zinc-200 z-50 pb-[env(safe-area-inset-bottom)]">
      <nav className="flex items-center justify-around h-16 px-2">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive =
            pathname === item.href ||
            (item.href !== '/' && pathname.startsWith(item.href));
          const showBadge = item.href === '/materialanfrage' && materialanfrageCount > 0;

          const highlightKey = HIGHLIGHT_KEY_FOR_ROUTE[item.href];
          const isHighlighted =
            highlightKey !== undefined &&
            highlights.has(highlightKey) &&
            !isActive &&
            !item.disabled;

          if (item.disabled) {
            return (
              <span
                key={item.href}
                className="flex flex-col items-center justify-center w-full h-full gap-1 text-zinc-300 cursor-not-allowed"
              >
                <div className="p-1.5"><Icon size={22} /></div>
                <span className="text-[10px] font-medium truncate w-full text-center px-1">{item.label}</span>
              </span>
            );
          }

          return (
            <Link
              key={item.href}
              href={item.href}
              className={clsx(
                'relative flex flex-col items-center justify-center w-full h-full gap-1 group transition-colors',
                isActive
                  ? 'text-primary'
                  : isHighlighted
                    ? 'text-green-700'
                    : 'text-zinc-500',
              )}
            >
              {isActive && (
                <span
                  aria-hidden="true"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-7 h-[3px] bg-primary rounded-b-[3px]"
                />
              )}
              <div
                className={clsx(
                  'p-1.5 rounded-full transition-colors relative',
                  isActive
                    ? 'bg-orange-50 text-primary'
                    : isHighlighted
                      ? 'bg-green-50 text-green-600'
                      : 'group-hover:bg-zinc-100 group-active:bg-zinc-200 text-zinc-500',
                )}
              >
                <Icon
                  size={22}
                  className={isActive ? 'fill-primary/20' : ''}
                />
                {showBadge && (
                  <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center border-2 border-white">
                    {materialanfrageCount}
                  </span>
                )}
              </div>
              <span
                className={clsx(
                  'text-[10px] font-medium truncate w-full text-center px-1',
                  isActive
                    ? 'text-primary font-bold'
                    : isHighlighted
                      ? 'text-green-700 font-semibold'
                      : 'text-zinc-500',
                )}
              >
                {item.label}
                {isHighlighted && <span className="sr-only"> (neu)</span>}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
